import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Icon, IconButton } from '../index';

import type { useTheme } from './theme';
import { rowIdOf } from './state/loggerDraft';
import type { GroupMember } from './supersetNotebook';
import { useTransientTip } from './useTransientTip';

// SupersetTabs — THE action bar: the first, always-present row of the
// FocusPanel card. It holds two things in a stable layout the user learns once
// (Antenore, inbox 7d3e33dc + 58aaf191, dal mockup):
//   • LEFT — the superset affordance. Standalone → a compact "＋ Superset"
//     pill (create/join). Grouped → a marking CUE (loop glyph + "N/M" position)
//     that reads "you're in a superset, exercise N of M". No group NAME, no
//     rename, no nav arrow on the line (the exercise name is the row below;
//     the superset has no visible name, so rename was removed entirely, AP-0n)
//     — tapping the cue cycles to the next member, the arrow's job without the
//     arrow's footprint.
//   • RIGHT — the exercise action cluster ⇄ replace / ❓ how-to / 🗑 remove, in
//     EVERY card state so the user never hunts for them when the state flips,
//     plus the ☰ superset menu only when grouped. These moved out of the old
//     FocusPanel title row (AP-0g), giving the exercise name its own line.
//   • MIDDLE (PE-31, prescribe only) — the WEEK cue, `weekCue`. Absent in the
//     logger, where there is no week to be at. Antenore put it here himself
//     (29/08): «tra superset e replace ci sta bene l'indicatore di settimana,
//     che risolve il problema di visibilità» — the D·W line above the roster,
//     which is what it replaces, was the wrong place and read as bolted on.
//
// The screen auto-advances A→B→… after each LOG SET; the cue tap is the manual
// override. State (done/in-progress/not-done) comes from groupMembersOf — data
// model untouched.
//
// ⭐ The superset cue and the week cue are ONE idiom, so they are one component
// (`PositionCue`, below): a glyph that names the axis, "N/M" in tabular figures,
// and a tap that steps forward and wraps. That tap is also the only door a
// screen reader or a keyboard has onto the week — the card's swipe reaches
// neither — which is why the week counter is a control here and not the plain
// text it was under `PagerNav`.
export function SupersetTabs({
  members,
  activeEntryId,
  groupLabel,
  resolveName,
  onSelect,
  onSplit,
  onCreate,
  onOpenMenu,
  editLocked,
  onSwap,
  onOpenTip,
  onRemoveExercise,
  weekCue,
  t,
  theme,
}: {
  /** Group members (≥1) when grouped; empty for a standalone exercise. */
  members: GroupMember[];
  /** WHICH MEMBER ROW is in focus — DraftEntry.entryId (LG-39). A circuit may
   *  legitimately hold the same exercise twice, so the slug cannot say which
   *  of them the cue is sitting on. */
  activeEntryId: string | null;
  /** Localized group label, e.g. "Superset 1" (from ctx.groupLabelFor) — used
   *  in the cue's a11y label only; not shown on the line. */
  groupLabel: string;
  resolveName: (slug: string, fallback: string) => string;
  onSelect: (entryId: string) => void;
  /** Splits/dissolves the group (☰ fallback when onOpenMenu is undefined). */
  onSplit: () => void;
  /** Standalone only: starts a superset. Always defined on a standalone
   *  exercise (AP-0k — the ＋ Superset pill must be discoverable even with one
   *  exercise); the screen routes it to the group picker when a partner exists,
   *  else the exercise picker to add one first. */
  onCreate?: () => void;
  /** The ☰ hamburger → open the superset management menu (save-template / add /
   *  reorder / remove / dissolve). Falls back to onSplit when undefined. */
  onOpenMenu?: () => void;
  /** Editing a past session: creating + dissolving a superset are forbidden
   *  (＋/☰ greyed + balloon). Member navigation + replace/remove stay live. */
  editLocked: boolean;
  /** ⇄ Replace the active exercise (opens the swap picker). */
  onSwap: () => void;
  /** ❓ Open the how-to quick-tip for the active exercise. */
  onOpenTip: () => void;
  /** 🗑 Remove the active exercise from the workout (parent confirms). */
  onRemoveExercise: () => void;
  /** PE-31 — prescribe only: where the card's week cursor is, and the step
   *  forward. Absent ⇒ nothing is drawn (presence-is-visibility, the same
   *  convention `onCreate`/`onOpenMenu` already use here) — the logger card
   *  has no week. ⛔ Never rendered on a one-week program: "W1/1" is a label
   *  that says nothing and a control that goes nowhere. */
  weekCue?: {
    index: number;
    count: number;
    onNext: () => void;
    onPrev: () => void;
  };
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const { showTip, tipNode } = useTransientTip(theme);
  const createLockMsg = t('mobile.timeline.createGroupLocked', {
    defaultValue: "Editing a past session: you can't create a superset.",
  });
  const splitLockMsg = t('mobile.timeline.splitGroupLocked', {
    defaultValue: "Editing a past session: you can't dissolve a superset.",
  });

  const grouped = members.length > 0;

  // Right-side action cluster — ⇄/❓/🗑 shared by every state via the shared
  // IconButton (no hand-rolled box). gap s3 + hitSlop {left,right:4} → 44pt
  // targets that meet exactly, no overlap (the retired title row's recipe); the
  // outer edges (⇄ left, 🗑 right in standalone) face free space so they take
  // the fuller 8 (a11y audit AP-0g).
  const clusterIcons = (
    <>
      {/* ⛔ PE-31 — no longer gated. `hideInert` hid ⇄/❓/＋Superset/☰ because
          PE-24 mounted none of their modals; PE-26 mounted all of them and the
          gate outlived its reason, so on the phone the prescribe card kept only
          the 🗑 [[feedback_a_gate_outlives_the_reason_that_lit_it]]. */}
      <IconButton
        name="arrow-left-right"
        onPress={onSwap}
        accessibilityLabel={t('mobile.logger.replace', {
          defaultValue: 'Replace',
        })}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 4 }}
        theme={theme}
      />
      <IconButton
        name="help"
        onPress={onOpenTip}
        accessibilityLabel={t('mobile.logger.descriptionToggle', {
          defaultValue: 'Show / hide description',
        })}
        hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
        theme={theme}
      />
      <IconButton
        name="trash"
        variant="destructive"
        onPress={onRemoveExercise}
        accessibilityLabel={t('workout.removeExercise', {
          defaultValue: 'Remove exercise',
        })}
        hitSlop={{ top: 8, bottom: 8, left: 4, right: grouped ? 4 : 8 }}
        theme={theme}
      />
    </>
  );

  // Left affordance.
  let left: ReactNode = null;
  if (!grouped) {
    // Standalone: the ＋ Superset pill (or nothing when there's no partner to
    // pair with — the cluster still needs to render, so we can't bail early).
    left = onCreate ? (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: editLocked }}
        accessibilityLabel={t('mobile.logger.addGroup', {
          defaultValue: 'Group with another exercise',
        })}
        onPress={editLocked ? () => showTip(createLockMsg) : onCreate}
        hitSlop={{ top: 8, bottom: 8 }}
        style={({ pressed }) => [
          styles.createLine,
          editLocked && styles.lineLocked,
          pressed && !editLocked && styles.createLinePressed,
        ]}
      >
        <Text style={[styles.createText, editLocked && styles.createTextLocked]}>
          {t('mobile.group.groupCta', { defaultValue: '+ Superset' })}
        </Text>
      </Pressable>
    ) : null;
  } else {
    const activePos = Math.max(
      0,
      members.findIndex((m) => rowIdOf(m.entry) === activeEntryId),
    );
    // Cycle forward, wrapping to the first member; null for a lone member.
    const cycleNext =
      members.length > 1 ? members[(activePos + 1) % members.length] : null;
    left = (
      <PositionCue
        theme={theme}
        styles={styles}
        glyph="repeat"
        position={activePos + 1}
        total={members.length}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 4 }}
        accessibilityLabel={
          cycleNext
            ? t('mobile.superset.cueCycle', {
                defaultValue:
                  '{{group}}, exercise {{pos}} of {{total}}. Tap to go to {{next}}.',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
                next: resolveName(
                  cycleNext.entry.exerciseId,
                  cycleNext.entry.exerciseName,
                ),
              })
            : t('mobile.superset.cueStatic', {
                defaultValue: '{{group}}, exercise {{pos}} of {{total}}',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
              })
        }
        onPress={
          cycleNext ? () => onSelect(rowIdOf(cycleNext.entry)) : undefined
        }
      />
    );
  }

  return (
    <>
      {tipNode}
      <View style={styles.bar}>
        {left}
        <View style={styles.spacer} />
        {/* PE-31 — the week cursor, between the superset affordance and the
            action cluster, exactly where Antenore put it. Two spacers, so it
            sits in the middle of the bar however wide the two ends are. */}
        {weekCue && weekCue.count > 1 ? (
          <PositionCue
            theme={theme}
            styles={styles}
            glyph="calendar"
            position={weekCue.index + 1}
            total={weekCue.count}
            hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
            // ⛔ Says which AXIS out loud. "2 of 4" beside a day×week grid
            // reads as a day, and the visible "W2/4" is a notation a screen
            // reader spells rather than speaks.
            accessibilityLabel={t('composer.weekCue', {
              defaultValue:
                'Week {{pos}} of {{total}}. Tap for the next week.',
              pos: weekCue.index + 1,
              total: weekCue.count,
            })}
            onPress={weekCue.onNext}
            // ⭐ THE WAY BACK, and it carries no pixels. The swipe reaches
            // neither VoiceOver nor a keyboard, so with a forward-only tap a
            // screen-reader user needed up to eleven of them to step back one
            // week on a 12-week cycle (a11y review, Major). ⛔ The cure is NOT
            // a second arrow — Antenore removed the arrows on purpose. It is a
            // named custom action plus a hold, the same pair the grid's own
            // holds already use (useGridGestures).
            onPrev={weekCue.onPrev}
            prevLabel={t('composer.weekCuePrev', {
              defaultValue: 'Previous week',
            })}
            // Same "W{n}" the grid's column heads use, so the two surfaces
            // name the axis the one way.
            prefix="W"
          />
        ) : null}
        <View style={styles.spacer} />
        <View style={styles.cluster}>
          {clusterIcons}
          {grouped ? (
            <IconButton
              name="menu"
              locked={editLocked}
              onPress={
                editLocked
                  ? () => showTip(splitLockMsg)
                  : (onOpenMenu ?? onSplit)
              }
              accessibilityLabel={t('mobile.superset.menuOpen', {
                defaultValue: 'Superset options',
              })}
              // Spoken lock reason (the ☰ stays an enabled button for a11y —
              // it's tappable-to-explain, not disabled).
              accessibilityHint={editLocked ? splitLockMsg : undefined}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
              theme={theme}
            />
          ) : null}
        </View>
      </View>
    </>
  );
}

// ⭐ The action bar's position marker, in ONE place: a glyph naming the axis,
// "N/M" in tabular figures, and a tap that steps forward. The superset cue
// (which exercise of the group) and the week cue (which week of the cycle) are
// the same control over two different axes — writing the second one out again
// would have been the copy §CONSOLIDAMENTO forbids, and the two would have
// drifted in box, gap and figure style the first time either was touched.
// ⛔ `onPress` absent ⇒ the cue is a READOUT: still announced, no longer a
// button, so a screen reader is never told to tap something that goes nowhere.
function PositionCue({
  theme,
  styles,
  glyph,
  position,
  total,
  prefix = '',
  accessibilityLabel,
  onPress,
  onPrev,
  prevLabel,
  hitSlop,
}: {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  glyph: 'repeat' | 'calendar';
  position: number;
  total: number;
  /** Prepended to the visible count only ("W2/4") — never to the spoken label,
   *  which says the axis in words. */
  prefix?: string;
  accessibilityLabel: string;
  onPress?: () => void;
  /** The step BACKWARD, with no glyph of its own: a hold, plus a named action
   *  so a screen reader can reach it. Absent on the superset cue, where the
   *  members are two or three and wrapping forward costs nothing. */
  onPrev?: () => void;
  /** Names `onPrev` for the screen reader. Required with it, ignored without. */
  prevLabel?: string;
  hitSlop: { top: number; bottom: number; left: number; right: number };
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={onPrev && prevLabel ? prevLabel : undefined}
      {...(onPrev && prevLabel
        ? {
            accessibilityActions: [{ name: 'decrement', label: prevLabel }],
            onAccessibilityAction: (e: {
              nativeEvent: { actionName: string };
            }) => {
              if (e.nativeEvent.actionName === 'decrement') onPrev();
            },
          }
        : {})}
      disabled={!onPress}
      onPress={onPress}
      onLongPress={onPrev}
      hitSlop={hitSlop}
      style={({ pressed }) => [
        styles.cue,
        pressed && onPress ? styles.cuePressed : null,
      ]}
    >
      <Icon
        name={glyph}
        size={theme.typography.sizes.md}
        color={theme.palette.ink}
      />
      <Text style={styles.cueCount}>
        {prefix}
        {position}/{total}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The action bar: left affordance + spacer + right cluster, one row, no
    // surrounding card (it lives inside the FocusPanel card).
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    spacer: { flex: 1 },
    // gap s3: each cluster icon's 4px horizontal hitSlop meets its neighbour's
    // exactly (44pt targets, zero overlap — the retired title-row recipe).
    cluster: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
    },

    // POSITION CUE — axis glyph + N/M. Same 36-box treatment as the
    // IconButtons beside it (surface2 + borderStrong, DS-9) so the bar reads as
    // one control family; the glyph is the axis marker (a SHAPE cue, never
    // colour-only — `repeat` = superset, `calendar` = week), the count is
    // neutral ink to dodge brand-on-fill contrast debt (DS-11).
    cue: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      minHeight: 36,
      paddingHorizontal: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface2,
    },
    cuePressed: { backgroundColor: p.surface },
    cueCount: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },

    // Standalone ＋ Superset — a COMPACT dashed pill (AP-0d, dal mockup: the
    // full-width banner shouted louder than the data rows below it). Hugs its
    // text, same 36 box the qualifier chips use.
    createLine: {
      minHeight: 36,
      justifyContent: 'center',
      paddingHorizontal: s.s3,
      borderRadius: r.r2,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    createLinePressed: { backgroundColor: p.surface2 },
    // base, not sm: legibility beats compactness on the label itself (the pill
    // already shrank around it). brand500-on-surface contrast is a token-level
    // issue tracked in the backlog (DS-11), not a per-site fix.
    createText: { color: p.brand500, fontSize: ty.sizes.base, fontWeight: '700' },

    // Edit-locked ＋ Superset: greyed but still Pressable (tap → balloon).
    lineLocked: { opacity: 0.4 },
    createTextLocked: { color: p.ink3 },
  });
}
